'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import GovernmentHero from './GovernmentHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — sourced from GovernmentHero
   (Granicus Citizen Experience Report 2025, Deloitte Government Trends 2026,
   World Bank AI Citizen Engagement 2024). */
const METRICS = [
  { value: '7,000+', label: 'government organisations worldwide now rely on unified citizen experience platforms to manage public communications (Granicus Citizen Experience Report 2025)' },
  { value: '64%', label: "of citizens globally say their government's responsiveness to social media feedback directly affects their trust in public institutions (Deloitte Government Trends 2026)" },
  { value: '3x', label: 'faster escalation of government misinformation to mainstream media vs. official response speed — the gap that structured monitoring closes (World Bank AI Citizen Engagement 2024)' },
];

export default function GovernmentBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('government-public-sector');

  const CHALLENGES = [
    {
      title: t("industries_government_public_sector.the_informal_grievance_channel"),
      desc: t("industries_government_public_sector.millions_of_citizens_post_complaint"),
    },
    {
      title: t("industries_government_public_sector.the_misinformation_speed_problem"),
      desc: t("industries_government_public_sector.government_misinformation_about_sch"),
    },
    {
      title: t("industries_government_public_sector.the_crisis_communication_gap"),
      desc: t("industries_government_public_sector.infrastructure_failures_service_dis"),
    },
    {
      title: t("industries_government_public_sector.the_scheme_launch_intelligence_gap"),
      desc: t("industries_government_public_sector.when_a_new_programme_is_announced_t"),
    },
  ];

  const FAQS = [
    {
      q: t("industries_government_public_sector.what_is_a_government_citizen_experi"),
      a: t("industries_government_public_sector.one_workspace_for_citizen_sentiment"),
    },
    {
      q: t("industries_government_public_sector.how_does_locobuzz_complement_formal"),
      a: t("industries_government_public_sector.locobuzz_captures_the_informal_soci"),
    },
    {
      q: t("industries_government_public_sector.how_does_locobuzz_detect_and_help_c"),
      a: t("industries_government_public_sector.real_time_keyword_alert_sets_fire_w"),
    },
    {
      q: t("industries_government_public_sector.can_locobuzz_monitor_citizen_sentim"),
      a: t("industries_government_public_sector.yes_multi_language_citizen_sentimen"),
    },
    {
      q: t("industries_government_public_sector.how_does_locobuzz_help_public_secto"),
      a: t("industries_government_public_sector.real_time_social_monitoring_across"),
    },
    {
      q: t("industries_government_public_sector.can_locobuzz_support_scheme_launch"),
      a: t("industries_government_public_sector.yes_scheme_launch_monitoring_tracks"),
    },
  ];

  const BG_TITLE = t('industries_government_public_sector.why_is_citizen_experience_now_a_str');
  const BG_ACCENT = 'Government Departments & Public Sector Organisations?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">
        {t("industries_government_public_sector.locobuzz_is_an_ainative_citizen_exp")}
      </span>
      <span className="sr-only">
        {t("industries_government_public_sector.formal_grievance_portals_capture_on")}
      </span>
      <span className="sr-only">
        {t("industries_government_public_sector.locobuzz_maps_six_purposebuilt_capa")}
      </span>
      <span className="sr-only">
        {t("industries_government_public_sector.government_and_public_sector_organi")}
      </span>

      <GovernmentHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t("industries_government_public_sector.why_locobuzz_government_public_sect")}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t("industries_government_public_sector.the_same_citizens_who_can_file_form")}
        imageSrc="/industries/why/government-public-sector.webp"
        imageAlt={t("industries_government_public_sector.government_citizen_sentiment_and_mu")}
        bg="tint"
      />

      <IndustryChallenges
        title={t("industries_government_public_sector.what_citizen_cx_communication_chall")}
        items={CHALLENGES}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="government-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
